import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IS_DEMO_MODE } from '../config/env';
import { getSecurityCapabilitiesForUser, type Capability } from '../lib/auth/capabilities';
import { supabase } from '../lib/supabase';
import { captureError } from '../services/MonitoringService';
import type { User } from '../types';

export const SECURITY_SCOPE_CAPABILITIES = [
  'security.overview.view',
  'security.identity.view',
  'security.identity.manage',
  'security.audit.view',
  'security.audit.export',
  'security.privacy.view',
  'security.privacy.manage',
  'security.integrations.view',
  'security.integrations.manage',
  'security.api_keys.view',
  'security.api_keys.manage',
  'security.encryption.view',
  'security.encryption.manage',
  'security.encryption.rotate',
  'security.incidents.view',
  'security.incidents.manage',
  'security.compliance.view',
  'security.compliance.manage',
  'security.assurance.view',
  'security.assurance.generate',
  'security.assurance.download',
  'security.privileged_access.approve',
] as const satisfies readonly Capability[];

export type SecurityScopeCapability = (typeof SECURITY_SCOPE_CAPABILITIES)[number];
export type SecurityAssuranceLevel = 'aal1' | 'aal2' | 'aal3';

export const SECURITY_AAL2_CAPABILITIES = new Set<SecurityScopeCapability>([
  'security.identity.manage',
  'security.audit.export',
  'security.privacy.manage',
  'security.integrations.manage',
  'security.api_keys.manage',
  'security.encryption.manage',
  'security.encryption.rotate',
  'security.incidents.manage',
  'security.compliance.manage',
  'security.assurance.generate',
  'security.assurance.download',
  'security.privileged_access.approve',
]);

export interface SecurityCapabilityDecision {
  granted: boolean;
  effective: boolean;
  requiresAal2: boolean;
  requiresActiveActivation: boolean;
  reason:
    | 'platform_capability_not_granted'
    | 'workspace_capability_not_granted'
    | 'aal2_required'
    | 'active_platform_activation_required'
    | null;
}

export interface SecurityScopeAuthority {
  scopePlane: 'platform' | 'workspace';
  workspaceId: string | null;
  aal: SecurityAssuranceLevel;
  capabilities: Record<SecurityScopeCapability, SecurityCapabilityDecision>;
  source: 'server' | 'demo';
}

const isRecord = (value: unknown): value is Record<string, unknown> => (
  typeof value === 'object' && value !== null && !Array.isArray(value)
);

function parseAuthorityResponse(
  value: unknown,
  requestedWorkspaceId: string | null,
): SecurityScopeAuthority {
  if (!isRecord(value) || !isRecord(value.capabilities)) {
    throw new Error('Security authority returned a malformed response.');
  }

  const expectedPlane = requestedWorkspaceId === null ? 'platform' : 'workspace';
  if (value.scope_plane !== expectedPlane) {
    throw new Error('Security authority returned the wrong control plane.');
  }
  if (requestedWorkspaceId === null) {
    if (value.workspace_id !== null) {
      throw new Error('Platform Security authority returned a workspace scope.');
    }
  } else if (value.workspace_id !== requestedWorkspaceId) {
    throw new Error('Security authority returned the wrong workspace scope.');
  }
  if (value.aal !== 'aal1' && value.aal !== 'aal2' && value.aal !== 'aal3') {
    throw new Error('Security authority returned an unknown assurance level.');
  }

  const capabilities = {} as Record<SecurityScopeCapability, SecurityCapabilityDecision>;
  for (const capability of SECURITY_SCOPE_CAPABILITIES) {
    const rawDecision = value.capabilities[capability];
    if (!isRecord(rawDecision)
      || typeof rawDecision.granted !== 'boolean'
      || typeof rawDecision.effective !== 'boolean'
      || typeof rawDecision.requires_aal2 !== 'boolean'
      || typeof rawDecision.requires_active_activation !== 'boolean'
      || (
        rawDecision.reason !== null
        && rawDecision.reason !== 'platform_capability_not_granted'
        && rawDecision.reason !== 'workspace_capability_not_granted'
        && rawDecision.reason !== 'aal2_required'
        && rawDecision.reason !== 'active_platform_activation_required'
      )) {
      throw new Error(`Security authority omitted a valid decision for ${capability}.`);
    }
    if (rawDecision.effective && !rawDecision.granted) {
      throw new Error(`Security authority returned an invalid effective decision for ${capability}.`);
    }
    if (rawDecision.effective && rawDecision.reason !== null) {
      throw new Error(`Security authority returned a denial reason for effective ${capability}.`);
    }
    if (rawDecision.granted && !rawDecision.effective && rawDecision.reason === null) {
      throw new Error(`Security authority omitted the lock reason for ${capability}.`);
    }
    if (rawDecision.requires_active_activation
      && (expectedPlane !== 'platform' || !rawDecision.requires_aal2)) {
      throw new Error(`Security authority returned an invalid activation requirement for ${capability}.`);
    }
    if (rawDecision.reason === 'active_platform_activation_required'
      && !rawDecision.requires_active_activation) {
      throw new Error(`Security authority returned an invalid activation denial for ${capability}.`);
    }
    capabilities[capability] = {
      granted: rawDecision.granted,
      effective: rawDecision.effective,
      requiresAal2: rawDecision.requires_aal2,
      requiresActiveActivation: rawDecision.requires_active_activation,
      reason: rawDecision.reason,
    };
  }

  return {
    scopePlane: expectedPlane,
    workspaceId: requestedWorkspaceId,
    aal: value.aal,
    capabilities,
    source: 'server',
  };
}

function buildDemoAuthority(
  user: User | null,
  requestedWorkspaceId: string | null,
  aal: SecurityAssuranceLevel,
): SecurityScopeAuthority {
  // Resolve at the strongest AAL to distinguish entitlement from session
  // effectiveness, then apply the same AAL2 policy exposed by the server RPC.
  const entitled = new Set(getSecurityCapabilitiesForUser(user, { aal: 'aal3' }));
  const hasAal2 = aal === 'aal2' || aal === 'aal3';
  const capabilities = {} as Record<SecurityScopeCapability, SecurityCapabilityDecision>;

  for (const capability of SECURITY_SCOPE_CAPABILITIES) {
    const granted = entitled.has(capability);
    const requiresAal2 = SECURITY_AAL2_CAPABILITIES.has(capability);
    const requiresActiveActivation = requestedWorkspaceId === null && requiresAal2;
    // Demo has no authoritative activation registry. Treat the verified demo
    // persona as pre-activated so demos remain usable without weakening the
    // connected staging/production authority contract.
    const effective = granted && (!requiresAal2 || hasAal2);
    capabilities[capability] = {
      granted,
      effective,
      requiresAal2,
      requiresActiveActivation,
      reason: !granted
        ? requestedWorkspaceId === null
          ? 'platform_capability_not_granted'
          : 'workspace_capability_not_granted'
        : effective
          ? null
          : 'aal2_required',
    };
  }

  return {
    scopePlane: requestedWorkspaceId === null ? 'platform' : 'workspace',
    workspaceId: requestedWorkspaceId,
    aal,
    capabilities,
    source: 'demo',
  };
}

export function useSecurityScopeAuthority({
  scopeWorkspaceId,
  scopeKey,
  user,
  demoAal,
  enabled = true,
}: {
  scopeWorkspaceId: string | null;
  scopeKey: string;
  user: User | null;
  demoAal: SecurityAssuranceLevel;
  enabled?: boolean;
}) {
  const [authority, setAuthority] = useState<SecurityScopeAuthority | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const requestGenerationRef = useRef(0);

  const refresh = useCallback(async () => {
    const generation = ++requestGenerationRef.current;
    if (!enabled) {
      setAuthority(null);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);

    if (IS_DEMO_MODE) {
      setAuthority(buildDemoAuthority(user, scopeWorkspaceId, demoAal));
      setLoading(false);
      return;
    }

    if (!supabase) {
      setAuthority(null);
      setError('Security authority is unavailable because the production data service is not configured.');
      setLoading(false);
      return;
    }

    try {
      const { data, error: rpcError } = await supabase.rpc('get_my_security_scope_authority', {
        p_workspace_id: scopeWorkspaceId,
      });
      if (rpcError) throw new Error(rpcError.message);
      const parsed = parseAuthorityResponse(data, scopeWorkspaceId);
      if (generation !== requestGenerationRef.current) return;
      setAuthority(parsed);
    } catch (caught) {
      if (generation !== requestGenerationRef.current) return;
      const message = caught instanceof Error ? caught.message : 'Unable to resolve Security authority.';
      setAuthority(null);
      setError(message);
      captureError('Security scope authority resolution failed', {
        level: 'warning',
        tags: { feature: 'security_scope_authority' },
        extra: { error: message, scopeKey },
      });
    } finally {
      if (generation === requestGenerationRef.current) setLoading(false);
    }
  }, [demoAal, enabled, scopeKey, scopeWorkspaceId, user]);

  useEffect(() => {
    void refresh();
    return () => {
      requestGenerationRef.current += 1;
    };
  }, [refresh]);

  const grantedCapabilities = useMemo<Capability[]>(() => (
    authority
      ? SECURITY_SCOPE_CAPABILITIES.filter((capability) => authority.capabilities[capability].granted)
      : []
  ), [authority]);
  const effectiveCapabilities = useMemo<Capability[]>(() => (
    authority
      ? SECURITY_SCOPE_CAPABILITIES.filter((capability) => authority.capabilities[capability].effective)
      : []
  ), [authority]);
  const aal2LockedCapabilities = useMemo<SecurityScopeCapability[]>(() => (
    authority
      ? SECURITY_SCOPE_CAPABILITIES.filter((capability) => (
          authority.capabilities[capability].granted
          && !authority.capabilities[capability].effective
          && authority.capabilities[capability].reason === 'aal2_required'
        ))
      : []
  ), [authority]);
  const activationLockedCapabilities = useMemo<SecurityScopeCapability[]>(() => (
    authority
      ? SECURITY_SCOPE_CAPABILITIES.filter((capability) => (
          authority.capabilities[capability].granted
          && !authority.capabilities[capability].effective
          && authority.capabilities[capability].reason === 'active_platform_activation_required'
        ))
      : []
  ), [authority]);

  return {
    authority,
    grantedCapabilities,
    effectiveCapabilities,
    aal2LockedCapabilities,
    activationLockedCapabilities,
    loading,
    error,
    refresh,
  };
}
